/**
 * Elite Edition — JS  v4
 * File: assets/elite-edition.js
 *
 * FIXES over v3:
 *  - Uses Shopify Section Rendering API so Dawn cart-drawer updates instantly
 *  - Actually injects fetched section HTML into the drawer DOM
 *  - Handles both cart-drawer AND cart-notification (Dawn config varies)
 *  - Keeps variant ID as string (avoids parseInt overflow on large IDs)
 *  - Prevents double event listeners on theme-editor reload
 *  - Re-enables button on every error path
 *  - True /cart redirect as last-resort fallback
 *
 * Flow:
 *  1. Variant button → syncs hidden input + price
 *  2. ATC form submit → Ajax POST (with sections param) → opens drawer
 *  3. Cart drawer: Dawn section render → fallback chain → /cart redirect
 *  4. Re-inits cleanly on shopify:section:load
 */

(function () {
  'use strict';

  /* ── Helpers ───────────────────────────────────── */
  var qs  = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var qsa = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  /* Marker to prevent double-binding on the same element */
  var BOUND = 'data-elite-bound';


  /* ════════════════════════════════════════════════
     1. VARIANT SELECTION
  ════════════════════════════════════════════════ */
  function initVariants(root) {
    qsa('.elite-card__variants', root).forEach(function (group) {
      if (group.getAttribute(BOUND) === 'v') return;
      group.setAttribute(BOUND, 'v');

      group.addEventListener('click', function (e) {
        var btn = e.target.closest('.elite-card__variant-btn');
        if (!btn || btn.classList.contains('is-soldout')) return;

        var card = btn.closest('.elite-card');
        if (!card) return;

        /* Active state */
        qsa('.elite-card__variant-btn', group).forEach(function (b) {
          b.classList.remove('is-selected');
        });
        btn.classList.add('is-selected');

        /* Sync hidden form input */
        var input = qs('.elite-card__variant-input', card);
        if (input && btn.dataset.variantId) {
          input.value = btn.dataset.variantId;
        }

        /* Update displayed price */
        var priceEl   = qs('.elite-card__price', card);
        var compareEl = qs('.elite-card__compare-price', card);

        if (priceEl && btn.dataset.variantPrice) {
          priceEl.textContent = btn.dataset.variantPrice;
        }
        if (compareEl) {
          if (btn.dataset.comparePrice) {
            compareEl.textContent = btn.dataset.comparePrice;
            compareEl.style.display = '';
          } else {
            compareEl.style.display = 'none';
          }
        }
      });
    });
  }


  /* ════════════════════════════════════════════════
     2. DETECT CART SECTION IDS
     Dawn embeds cart-drawer / cart-icon-bubble as
     sections whose IDs we can read from the DOM.
     We pass them in the /cart/add.js POST so Shopify
     returns pre-rendered HTML we can swap in.
  ════════════════════════════════════════════════ */
  function getCartSectionIds() {
    var ids  = [];
    var seen = {};

    function add(el) {
      if (!el) return;
      var sec = el.closest('[id^="shopify-section-"]');
      if (!sec) return;
      var id = sec.id.replace('shopify-section-', '');
      if (!seen[id]) { ids.push(id); seen[id] = true; }
    }

    /* Dawn cart-drawer section */
    add(document.querySelector('cart-drawer'));

    /* Dawn cart-notification section */
    add(document.querySelector('cart-notification'));

    /* Cart icon / bubble section (header) */
    add(
      document.querySelector('.cart-count-bubble') ||
      document.querySelector('[data-cart-count]')  ||
      document.querySelector('.js-cart-count')
    );

    return ids;
  }


  /* ════════════════════════════════════════════════
     3. INJECT SECTION HTML
     Takes the `sections` object from the /cart/add.js
     response and swaps each section's innerHTML in
     the live DOM so the drawer shows updated items.
  ════════════════════════════════════════════════ */
  function injectSections(sectionsObj) {
    if (!sectionsObj) return;

    Object.keys(sectionsObj).forEach(function (id) {
      var wrapper = document.getElementById('shopify-section-' + id);
      if (!wrapper) return;

      var html = sectionsObj[id];

      /* Parse into a temp container */
      var temp = document.createElement('div');
      temp.innerHTML = html;

      /* If Shopify wraps in its own shopify-section div, unwrap */
      var inner = temp.querySelector('[id="shopify-section-' + id + '"]');
      if (inner) {
        wrapper.innerHTML = inner.innerHTML;
      } else {
        wrapper.innerHTML = html;
      }
    });
  }


  /* ════════════════════════════════════════════════
     4. OPEN CART DRAWER
     Ordered by likelihood:
       a) Dawn <cart-drawer> web component
       b) Generic drawer selectors
       c) Dawn <cart-notification> web component
       d) Custom events (Dawn + third-party themes)
       e) window.theme.cart (Prestige, Impulse, etc.)
       f) Shopify.theme.cartDrawerToggle (older)
       g) Click the theme's cart icon trigger
       h) Last resort: redirect to /cart
  ════════════════════════════════════════════════ */
  function openCartDrawer() {
    var opened = false;

    /* ── a) Dawn <cart-drawer> web component ───── */
    var drawerEl = document.querySelector('cart-drawer');

    if (drawerEl) {
      /* Dawn's CartDrawer defines .open() */
      if (typeof drawerEl.open === 'function') {
        try { drawerEl.open(); opened = true; } catch (e) {}
      }
      /* Ensure visibility via attributes regardless */
      drawerEl.removeAttribute('hidden');
      drawerEl.setAttribute('open', '');
      drawerEl.classList.add('active');
      if (!opened) opened = true;
    }

    /* ── b) Generic drawer selectors ── */
    if (!opened) {
      var genericDrawer =
        document.querySelector('[data-cart-drawer]') ||
        document.querySelector('#cart-drawer')       ||
        document.querySelector('.cart-drawer');

      if (genericDrawer) {
        genericDrawer.removeAttribute('hidden');
        genericDrawer.setAttribute('open', '');
        genericDrawer.classList.add('active', 'is-open');
        if (typeof genericDrawer.open === 'function') {
          try { genericDrawer.open(); } catch (e) {}
        }
        opened = true;
      }
    }

    /* ── c) Dawn <cart-notification> component ── */
    if (!opened) {
      var notifEl = document.querySelector('cart-notification');
      if (notifEl) {
        notifEl.removeAttribute('hidden');
        notifEl.setAttribute('open', '');
        notifEl.classList.add('active');
        if (typeof notifEl.open === 'function') {
          try { notifEl.open(); } catch (e) {}
        }
        opened = true;
      }
    }

    /* ── d) Custom events (always fire — harmless if ignored) ── */
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh',       { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:open',          { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:build',         { bubbles: true }));
      document.body.dispatchEvent(new CustomEvent('cart-drawer:open',   { bubbles: true }));
      document.body.dispatchEvent(new CustomEvent('ajaxProduct:added',  { bubbles: true }));
      document.body.dispatchEvent(new CustomEvent('product:added',      { bubbles: true }));
    } catch (e) {}

    /* ── e) Prestige / Impulse / Blockshop ── */
    if (!opened && window.theme && window.theme.cart) {
      try {
        if (typeof window.theme.cart.openDrawer === 'function') {
          window.theme.cart.openDrawer(); opened = true;
        } else if (typeof window.theme.cart.open === 'function') {
          window.theme.cart.open(); opened = true;
        }
      } catch (e) {}
    }

    /* ── f) Shopify.theme.cartDrawerToggle (older themes) ── */
    if (!opened && window.Shopify && window.Shopify.theme) {
      try {
        if (typeof window.Shopify.theme.cartDrawerToggle === 'function') {
          window.Shopify.theme.cartDrawerToggle(); opened = true;
        }
      } catch (e) {}
    }

    /* ── g) Click theme cart icon / trigger ── */
    if (!opened) {
      var trigger =
        document.querySelector('[data-cart-toggle]')   ||
        document.querySelector('[data-open-cart]')     ||
        document.querySelector('.js-cart-toggle')      ||
        document.querySelector('.js-drawer-open-cart') ||
        document.querySelector('a.cart-link')          ||
        document.querySelector('a.site-header__cart');

      if (trigger) {
        try { trigger.click(); opened = true; } catch (e) {}
      }
    }

    /* ── h) True last resort: go to /cart page ── */
    if (!opened) {
      window.location.href = '/cart';
    }
  }


  /* ════════════════════════════════════════════════
     5. REFRESH CART SECTION HTML (fallback)
     For themes / situations where /cart/add.js
     didn't return sections, fetch them separately.
  ════════════════════════════════════════════════ */
  function refreshCartSectionFallback() {
    var sectionIds = getCartSectionIds();
    if (sectionIds.length === 0) return;

    var url = '/cart?sections=' + sectionIds.join(',');

    fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (sections) { injectSections(sections); })
      .catch(function () { /* silent */ });
  }


  /* ════════════════════════════════════════════════
     6. UPDATE CART COUNT BADGES
     Queries /cart.js and writes item_count into every
     known cart-count element across themes.
  ════════════════════════════════════════════════ */
  function refreshCartCount() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = cart.item_count;
        var selectors = [
          '[data-cart-count]',
          '[data-cart-item-count]',
          '.cart-count',
          '.cart__count',
          '#cart-count',
          '.header__cart-count',
          '.CartCount',
          '.cart-count-bubble span',
          '.js-cart-count',
          'cart-count span'
        ];
        selectors.forEach(function (sel) {
          document.querySelectorAll(sel).forEach(function (el) {
            el.textContent = count;
            if (count > 0) {
              el.removeAttribute('hidden');
              /* Some themes hide the parent bubble container */
              var parent = el.parentElement;
              if (parent && parent.classList.contains('cart-count-bubble')) {
                parent.removeAttribute('hidden');
              }
            }
          });
        });
      })
      .catch(function () {});
  }


  /* ════════════════════════════════════════════════
     7. AJAX ADD TO CART
     Intercepts form submit → POST to /cart/add.js
     with Section Rendering API → injects HTML →
     opens drawer.
     Falls back gracefully at every stage.
  ════════════════════════════════════════════════ */
  function initATC(root) {
    qsa('.elite-card__atc-form', root).forEach(function (form) {
      /* Prevent double-binding */
      if (form.getAttribute(BOUND) === 'a') return;
      form.setAttribute(BOUND, 'a');

      form.addEventListener('submit', function (e) {
        e.preventDefault();

        var btn    = qs('.elite-card__atc',          form);
        var textEl = qs('.shine-btn__text',           form);
        if (!btn || btn.disabled) return;

        var input = qs('.elite-card__variant-input', form);
        if (!input || !input.value) {
          /* No variant id → native submit (safety) */
          form.submit();
          return;
        }

        /*
         * Keep variant ID as string-safe number.
         * Shopify IDs can exceed Number.MAX_SAFE_INTEGER, but
         * /cart/add.js accepts either number or string in JSON.
         */
        var variantId    = input.value.trim();
        var originalText = textEl ? textEl.textContent.trim() : 'Add to Cart';

        /* ── Loading state ── */
        btn.classList.add('is-loading');
        btn.disabled = true;
        if (textEl) textEl.textContent = 'Adding…';

        /* Build POST body — include section IDs for Section Rendering API */
        var sectionIds = getCartSectionIds();
        var postBody   = {
          id       : variantId,
          quantity : 1
        };

        /* Dawn and modern themes use the sections param to get
           pre-rendered HTML back in the same response. */
        if (sectionIds.length > 0) {
          postBody.sections     = sectionIds.join(',');
          postBody.sections_url = window.location.pathname;
        }

        fetch('/cart/add.js', {
          method  : 'POST',
          headers : {
            'Content-Type'     : 'application/json',
            'Accept'           : 'application/json',
            'X-Requested-With' : 'XMLHttpRequest'
          },
          body : JSON.stringify(postBody)
        })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (err) {
              throw new Error(err.description || err.message || 'Could not add to cart');
            });
          }
          return res.json();
        })
        .then(function (data) {
          /* ── Success ── */
          btn.classList.remove('is-loading');
          btn.classList.add('is-success');
          if (textEl) textEl.textContent = 'Added ✓';

          /* Inject updated section HTML returned by Shopify */
          if (data && data.sections) {
            injectSections(data.sections);
          } else {
            /* Fallback: fetch sections separately */
            refreshCartSectionFallback();
          }

          /* Open the drawer (HTML already injected above) */
          openCartDrawer();

          /* Update header cart count badge */
          refreshCartCount();

          /* Reset button after short delay */
          setTimeout(function () {
            btn.classList.remove('is-success');
            btn.disabled = false;
            if (textEl) textEl.textContent = originalText;
          }, 2200);
        })
        .catch(function (err) {
          console.warn('[EliteEdition] ATC error:', err.message || err);

          /* Always restore button so user can retry */
          btn.classList.remove('is-loading');
          btn.disabled = false;

          /* Brief error message */
          if (textEl) {
            textEl.textContent = err.message || 'Error — try again';
            setTimeout(function () {
              if (textEl) textEl.textContent = originalText;
            }, 2000);
          }

          /* Native form submit as ultimate fallback */
          try {
            form.submit();
          } catch (ex) {
            window.location.href = '/cart';
          }
        });
      });
    });
  }


  /* ════════════════════════════════════════════════
     8. INIT
  ════════════════════════════════════════════════ */
  function init(root) {
    initVariants(root || document);
    initATC(root || document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* Theme editor: section:load fires when a section
     is added or re-rendered — reinit only that section.
     The BOUND attribute prevents duplicate listeners. */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });

})();